import { useEffect, useId, useState, type ReactNode } from 'react'
import {
	AlertCircle,
	Building2,
	CheckSquare,
	FolderKanban,
	FolderPlus,
	ListChecks,
	Mail,
	MapPin,
	Pencil,
	Presentation,
	Table2,
	Target,
	Trash2,
	UserPlus,
	type LucideIcon,
} from 'lucide-react'

import { Badge } from 'components/ui/badge'
import { Input } from 'components/ui/input'
import { Select } from 'components/ui/select'
import { cn } from 'lib/utils'
import type { AiPlanBody, AiPlanCandidate, AiPlanGap, AiPlanProblem, AiPlanStep, AiRefInfo } from 'services/aiService'

import { describeStep, placeAddress, sentenceText } from '../plan/planSteps'
import { CandidatePicker } from './CandidatePicker'
import { EmailPrompt } from './EmailPrompt'

/**
 * One step of a plan (MYD-623): an icon and a sentence, the gaps the user must
 * fill (a choice, an email), and an editor for what may be changed.
 *
 * Every change is handed up as the whole next step; the card saves it. Text
 * fields commit on blur or Enter, so typing a name is one save, not one per
 * keystroke; selects and pickers commit as they change.
 */

const ICONS: Record<string, LucideIcon> = {
	'location.create': MapPin,
	'location.useExisting': MapPin,
	'project.create': FolderKanban,
	'asset.addPartner': UserPlus,
	'partner.inviteByEmail': Mail,
	'task.create': CheckSquare,
	'task.applyTemplate': ListChecks,
	'evaluation.create': Target,
	'evaluationSite.addFromLocation': Building2,
	'evaluationSite.addFromPlace': Building2,
	'explorerReport.createFromTemplate': Table2,
	'explorerReport.createFromDefinition': Table2,
	'presentation.createFromTemplate': Presentation,
	'portfolio.addLocation': FolderPlus,
}

/** Ops whose `name` the user may change. */
const NAMED = new Set(['location.create', 'project.create', 'task.create', 'evaluation.create', 'evaluationSite.addFromPlace', 'explorerReport.createFromDefinition'])

const PRIORITIES: [string, string][] = [
	['P1', 'P1 - Urgent'],
	['P2', 'P2 - High'],
	['P3', 'P3 - Medium'],
	['P4', 'P4 - Low'],
]

const CANDIDATE_LEGENDS: Record<string, string> = {
	placeRef: 'Which place did you mean?',
	partnerRef: 'Which person did you mean?',
	assigneeRef: 'Who should it be assigned to?',
	locationRef: 'Which location did you mean?',
	projectRef: 'Which project did you mean?',
}

export interface PlanStepRowProps {
	step: AiPlanStep
	index: number
	plan: AiPlanBody
	refs: Record<string, AiRefInfo>
	gaps: AiPlanGap[]
	problems: AiPlanProblem[]
	projectTypes: readonly string[]
	disabled?: boolean
	onChange: (next: AiPlanStep) => void
	onRemove: () => void
}

/** A text field that commits on blur or Enter, and only when it changed. */
function CommitInput({ label, value, onCommit, disabled, type = 'text' }: { label: string; value: string; onCommit: (value: string) => void; disabled?: boolean; type?: string }) {
	const [draft, setDraft] = useState(value)
	const id = useId()
	// The saved value comes back from the server: show it.
	useEffect(() => setDraft(value), [value])
	const commit = () => {
		const next = draft.trim()
		if (next && next !== value) onCommit(next)
		else setDraft(value)
	}
	return (
		<div className="space-y-1">
			<label htmlFor={id} className="text-xs font-medium text-gray-600">
				{label}
			</label>
			<Input
				id={id}
				type={type}
				value={draft}
				disabled={disabled}
				onChange={event => setDraft(event.target.value)}
				onBlur={commit}
				onKeyDown={event => {
					if (event.key === 'Enter') {
						event.preventDefault()
						commit()
					}
				}}
				className="h-8"
			/>
		</div>
	)
}

function Field({ label, children }: { label: string; children: (id: string) => ReactNode }) {
	const id = useId()
	return (
		<div className="space-y-1">
			<label htmlFor={id} className="text-xs font-medium text-gray-600">
				{label}
			</label>
			{children(id)}
		</div>
	)
}

/** Partners this conversation found, for an assignee picker. */
const partnersIn = (refs: Record<string, AiRefInfo>): AiPlanCandidate[] =>
	Object.entries(refs)
		.filter(([, info]) => info.kind === 'partner')
		.map(([ref, info]) => ({ ref, label: info.label }))

/**
 * The tasks a template will add (MYD-637), so loading one is not a leap of
 * faith: the first few by sequence, from the ref the template was issued as,
 * and how many more. The total is in the ref's label, "Remodel (14 tasks)".
 */
function TemplateTaskList({ info }: { info?: AiRefInfo }) {
	const items = info?.items ?? []
	if (!items.length) return null
	const total = Number(info?.label.match(/\((\d+) tasks?\)$/)?.[1] ?? items.length)
	return (
		<div className="mt-2 ml-8">
			<p className="text-xs font-medium text-gray-600">Tasks it adds</p>
			<ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs text-gray-700">
				{items.map((item, n) => (
					<li key={n}>{item}</li>
				))}
			</ul>
			{total > items.length && <p className="mt-0.5 text-xs text-gray-500">and {total - items.length} more</p>}
		</div>
	)
}

export function PlanStepRow({ step, index, plan, refs, gaps, problems, projectTypes, disabled, onChange, onRemove }: PlanStepRowProps) {
	const [editing, setEditing] = useState(false)
	const editorId = useId()
	const Icon = ICONS[step.op] ?? Pencil
	const phrases = describeStep(step, plan, refs)
	const { args } = step
	const set = (patch: Record<string, unknown>) => onChange({ ...step, args: { ...args, ...patch } })

	// A choice made is a gap closed: the candidates go, and so does the field's needsInput.
	const pick = (field: string, ref: string) => {
		const { [field]: _chosen, ...candidates } = step.candidates ?? {}
		onChange({
			...step,
			args: { ...args, [field]: ref },
			candidates: Object.keys(candidates).length ? candidates : undefined,
			needsInput: step.needsInput.filter(name => name !== field),
		})
	}

	// "Use your existing location" turns the step into using it.
	const useExisting = (locationRef: string) =>
		onChange({ id: step.id, op: 'location.useExisting', args: { locationRef }, dependsOn: step.dependsOn, needsInput: [], label: step.label })

	const existingHere: AiPlanCandidate[] =
		step.op === 'location.create'
			? Object.entries(refs)
					.filter(([, info]) => info.kind === 'location' && info.near && (info.near === args.placeRef || (step.candidates?.placeRef ?? []).some(option => option.ref === info.near)))
					.map(([ref, info]) => ({ ref, label: info.label }))
			: []

	const needsEmail = step.op === 'partner.inviteByEmail' && (!args.email || step.needsInput.includes('email'))
	const hasEditor = NAMED.has(step.op) || ['project.create', 'task.create', 'asset.addPartner', 'partner.inviteByEmail'].includes(step.op)
	const waiting = gaps.length > 0 || problems.length > 0
	const projectTypeIsNew = step.op === 'project.create' && Boolean(args.projectType) && !projectTypes.includes(args.projectType)

	return (
		<li className={cn('rounded-lg border bg-white p-3', waiting ? 'border-warning' : 'border-gray-200')} aria-label={`Step ${index + 1}: ${sentenceText(phrases)}`}>
			<div className="flex items-start gap-2.5">
				<span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary" aria-hidden="true">
					<Icon className="h-3.5 w-3.5" />
				</span>
				<div className="min-w-0 flex-1 text-sm leading-6 text-gray-800">
					{phrases.map((phrase, n) =>
						phrase.strong ? (
							<strong key={n} className="font-semibold text-gray-900">
								{phrase.text}
							</strong>
						) : (
							<span key={n}>{phrase.text}</span>
						),
					)}
					{projectTypeIsNew && (
						<Badge variant="outline" className="ml-1.5 px-1.5 py-0 align-middle text-[10px]" title="A project type you have not used before; it will be added">
							New type
						</Badge>
					)}
				</div>
				<div className="flex shrink-0 items-center gap-0.5">
					{hasEditor && (
						<button
							type="button"
							onClick={() => setEditing(open => !open)}
							disabled={disabled}
							aria-expanded={editing}
							aria-controls={editorId}
							aria-label={`Edit step ${index + 1}`}
							className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
						>
							<Pencil className="h-3.5 w-3.5" />
						</button>
					)}
					<button
						type="button"
						onClick={onRemove}
						disabled={disabled}
						aria-label={`Remove step ${index + 1}`}
						className="rounded p-1.5 text-gray-500 hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
					>
						<Trash2 className="h-3.5 w-3.5" />
					</button>
				</div>
			</div>

			{Object.entries(step.candidates ?? {}).map(([field, options]) => (
				<CandidatePicker
					key={field}
					legend={CANDIDATE_LEGENDS[field] ?? 'Which did you mean?'}
					options={options}
					value={args[field]}
					refs={refs}
					existing={field === 'placeRef' ? existingHere : []}
					disabled={disabled}
					onPick={ref => pick(field, ref)}
					onUseExisting={useExisting}
				/>
			))}

			{/* Already-settled place with a location the user has there: still offer it. */}
			{step.op === 'location.create' && !step.candidates?.placeRef && existingHere.length > 0 && (
				<CandidatePicker
					legend="You may already have this location"
					options={[{ ref: args.placeRef, label: 'Create a new location here' }]}
					value={args.placeRef}
					refs={refs}
					existing={existingHere}
					disabled={disabled}
					onPick={() => {}}
					onUseExisting={useExisting}
				/>
			)}

			{step.op === 'task.applyTemplate' && <TemplateTaskList info={refs[args.templateRef]} />}

			{needsEmail && <EmailPrompt name={args.name} value={args.email} disabled={disabled} onSave={email => onChange({ ...step, args: { ...args, email }, needsInput: step.needsInput.filter(name => name !== 'email') })} />}

			{editing && hasEditor && (
				<div id={editorId} className="mt-3 grid gap-3 border-t border-gray-100 pt-3 sm:grid-cols-2">
					{NAMED.has(step.op) && <CommitInput label="Name" value={args.name ?? ''} disabled={disabled} onCommit={name => set({ name })} />}

					{step.op === 'location.create' && (
						<div className="space-y-1">
							<span className="text-xs font-medium text-gray-600">Address</span>
							{/* Fixed by the place picked: change the place, not the address. */}
							<p className="text-sm text-gray-700">{placeAddress(refs[args.placeRef]?.place) || 'From the place you picked'}</p>
						</div>
					)}

					{step.op === 'project.create' && (
						<Field label="Project type">
							{id => (
								<Select id={id} value={args.projectType} disabled={disabled} onChange={event => set({ projectType: event.target.value })} className="h-8">
									{[...new Set([...projectTypes, args.projectType].filter(Boolean))].map(type => (
										<option key={type} value={type}>
											{type}
											{projectTypes.includes(type) ? '' : ' (new type)'}
										</option>
									))}
								</Select>
							)}
						</Field>
					)}

					{step.op === 'task.create' && (
						<>
							<CommitInput label="Due date" type="date" value={args.dueDate ?? ''} disabled={disabled} onCommit={dueDate => set({ dueDate })} />
							<Field label="Assigned to">
								{id => (
									<Select
										id={id}
										value={args.assigneeRef ?? ''}
										disabled={disabled}
										onChange={event => set({ assigneeRef: event.target.value || undefined })}
										className="h-8"
									>
										<option value="">Me</option>
										{partnersIn(refs).map(partner => (
											<option key={partner.ref} value={partner.ref}>
												{partner.label}
											</option>
										))}
									</Select>
								)}
							</Field>
							<Field label="Priority">
								{id => (
									<Select id={id} value={args.priority ?? ''} disabled={disabled} onChange={event => set({ priority: event.target.value || undefined })} className="h-8">
										<option value="">Default</option>
										{PRIORITIES.map(([value, text]) => (
											<option key={value} value={value}>
												{text}
											</option>
										))}
									</Select>
								)}
							</Field>
						</>
					)}

					{(step.op === 'asset.addPartner' || step.op === 'partner.inviteByEmail') && (
						<Field label="Access">
							{id => (
								<Select id={id} value={args.accessLevel ?? 'full'} disabled={disabled} onChange={event => set({ accessLevel: event.target.value })} className="h-8">
									<option value="full">Full access</option>
									<option value="readonly">Read-only</option>
								</Select>
							)}
						</Field>
					)}

					{step.op === 'partner.inviteByEmail' && !needsEmail && (
						<CommitInput label="Email" type="email" value={args.email ?? ''} disabled={disabled} onCommit={email => set({ email })} />
					)}
				</div>
			)}

			{(gaps.length > 0 || problems.length > 0) && (
				<ul className="mt-2 space-y-1">
					{problems.map((problem, n) => (
						<li key={`p${n}`} className="flex items-start gap-1.5 text-xs text-destructive">
							<AlertCircle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
							{problem.message}
						</li>
					))}
					{/* Gaps the pickers and prompt above do not already ask about. */}
					{gaps
						.filter(gap => !(gap.field === 'email' && needsEmail) && !step.candidates?.[gap.field])
						.map(gap => (
							<li key={`${gap.field}`} className="flex items-start gap-1.5 text-xs text-gray-700">
								<AlertCircle className="mt-0.5 h-3 w-3 shrink-0 text-warning" aria-hidden="true" />
								{gap.reason === 'choose one' ? `Choose the ${gap.field}.` : `Fill in the ${gap.field}.`}
							</li>
						))}
				</ul>
			)}
		</li>
	)
}

export default PlanStepRow
